import { useTranslate } from '../../application/i18n/LocaleProvider';
import { carName } from '../../domain/cars/carCatalogue';
import { catalogueTake, hasOwnGround } from '../../domain/cars/carSchema';
import { artSource } from '../cars/carArt';

/**
 * One of the two cars, as something to tap (SPEC-007 D-16, D-18).
 *
 * The card shows the artwork, the name and the year, and NEVER a figure — not
 * before the answer, which is the game, and not after it either. The numbers
 * live in the shared gauge below the pair, because two figures printed in two
 * separate boxes are two things to compare and one gauge is the comparison
 * already made (D-12).
 *
 * That leaves the card doing exactly one job in both states: being the picture
 * of a car you are choosing between, and then the picture of the one that won.
 * It never changes height, so the pair does not move under the tap.
 *
 * `aria-disabled` rather than `disabled`: a `disabled` element drops focus on
 * the spot in several engines, so a keyboard reader would answer the question
 * and lose their place on the page. The handler is guarded instead — the run
 * ignores a second answer anyway, and this is what keeps the card announceable
 * while it is being read.
 *
 * Two treatments, exactly as the garage card takes them: a front view brings its
 * own lit ground and goes full bleed, a side cut-out sits on a plate tinted by
 * the car's own paint.
 *
 * The card is DEALT rather than rendered: it drops onto the stage from above
 * with the weight the brand's motion band asks for — front-loaded, overshooting,
 * settling — and the second one lands a beat after the first. That is what the
 * `side` prop is for, and it is the only thing this component knows about where
 * it sits.
 */
export default function DuelOption({ car, side = 0, outcome, isChosen, onChoose }) {
    const t = useTranslate();
    const take = catalogueTake(car);
    const art = artSource(car, take);
    const revealed = Boolean(outcome);
    const className = [
        'duel-option',
        hasOwnGround(take) ? 'duel-option--grounded' : '',
        outcome ? `duel-option--${outcome}` : '',
        isChosen ? 'duel-option--chosen' : '',
    ].filter(Boolean).join(' ');

    return (
        <button
            type="button"
            className={className}
            style={{
                '--livery': `var(--livery-${car.livery})`,
                // Which side of the ring this one comes in on: the second card
                // lands a beat after the first, so the pair reads as two
                // arrivals rather than one block appearing.
                '--duel-side': side,
            }}
            onClick={() => !revealed && onChoose(car.id)}
            aria-disabled={revealed ? 'true' : undefined}
        >
            <span className="duel-option__stage">
                {art ? (
                    <img
                        className="duel-option__art"
                        src={art.src}
                        srcSet={art.srcSet}
                        /* THE PAIR'S OWN WIDTH, restated for a parser that runs
                           BEFORE the stylesheet.
                           ------------------------------------------------
                           `sizes` is read at preload time, so a card widened to
                           730px while this still said 300px would fetch the 480
                           rung and upscale it — a wide screen that looks WORSE
                           than the phone, with no error anywhere. This is the
                           mechanism the whole high-resolution story hangs on and
                           it fails silently in the one direction nobody checks.

                           Each step is `(stage - track) / 2` at the WIDEST the
                           stage gets in that band — the pair spans the full
                           stage above 1024 (`duelRevealScale.css`), and the
                           track is the VS gap between the two cards. 1120 stage
                           less an 80px track is 520 a card; 1400 less 104 is
                           648; 1680 less 128 is 776. Change either sheet and
                           this line is part of the change. */
                        sizes="(min-width: 1920px) 760px, (min-width: 1440px) 650px, (min-width: 1024px) 520px, (min-width: 640px) 300px, 45vw"
                        /* The name is right beside it and the button already
                           carries both; announcing the picture too would say
                           the car twice on every option. */
                        alt=""
                        /* Both are above the fold and both are the reason to
                           look — there is nothing below them worth the
                           bandwidth first. */
                        loading="eager"
                        decoding="async"
                    />
                ) : null}

                {outcome === 'winner' ? (
                    <span className="duel-option__ribbon">{t('duel.winner')}</span>
                ) : null}

                {/* Which one they actually tapped, and ONLY on a card that
                    lost. A reader who got it wrong is otherwise looking at two
                    cards, one marked winner, with nothing saying which was
                    theirs — but on the winning card the mark is the same fact
                    twice, stacked over the picture of the car it is naming.

                    On a round the clock ran out on neither card carries it,
                    which is itself the honest answer: nobody picked. */}
                {isChosen && outcome === 'loser' ? (
                    <span className="duel-option__pick">{t('duel.yourPick')}</span>
                ) : null}
            </span>

            <span className="duel-option__head">
                <span className="duel-option__name">{carName(car)}</span>
                <span className="duel-option__year figure">{car.year}</span>
            </span>
        </button>
    );
}
